import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";

const spotlightFeatures = [
  {
    id: "auto-planning",
    badge: "Auto Planning",
    badgeColor: "bg-[#3eb298]/10 text-[#3eb298]",
    title: "URLひとつで、広告戦略を自動設計。",
    description:
      "商品ページのURLを入力するだけで、AIが商材情報を自動で読み取り、競合との差別化ポイント、ターゲットペルソナ、訴求メッセージを構築。広告プランナーが数時間かけていた仕事を、数分で完了します。",
    details: [
      "商材の特徴・強み・価格帯を自動抽出",
      "競合商品との差別化ポイントを整理",
      "ターゲットユーザーのペルソナを設計",
      "複数の訴求軸とコピーを自動生成",
    ],
    screenshot: "/lp/screenshots/ui-product-select-form.png",
    screenshotAlt: "Auto Planning UI",
  },
  {
    id: "magic-resize",
    badge: "Magic Resize",
    badgeColor: "bg-[#5a69c1]/10 text-[#5a69c1]",
    title: "1枚作れば、全サイズに自動展開。",
    description:
      "Instagram、Facebook、Google、LINE、X（Twitter）、TikTok — 各媒体に必要なサイズを、ボタンひとつで一括生成。縦横比に合わせてレイアウトを再構成するため、単なるリサイズではなく、各サイズに最適化されたバナーが完成します。",
    details: [
      "主要広告媒体の全サイズに対応",
      "縦横比に応じたレイアウト自動再構成",
      "テキスト・画像の配置を最適化",
      "追加費用ゼロ、何サイズでも生成可能",
    ],
    screenshot: "/lp/screenshots/ui-magic-resize.png",
    screenshotAlt: "Magic Resize UI",
  },
  {
    id: "ai-score",
    badge: "AI Score",
    badgeColor: "bg-[#8f6fc2]/10 text-[#8f6fc2]",
    title: "出稿前に、勝てるバナーがわかる。",
    description:
      "100万件以上の広告配信データから構築されたAIモデルが、生成されたバナーの配信効果を事前にスコアリング。制作段階で「どのクリエイティブが勝つか」を予測し、検証の精度と効率を大幅に引き上げます。",
    details: [
      "100万件以上のデータから効果を予測",
      "視認性・訴求力・クリック率をスコア化",
      "改善ポイントを具体的にフィードバック",
      "A/Bテスト前に勝ちパターンを絞り込み",
    ],
    screenshot: "/lp/screenshots/ui-banner-detail.png",
    screenshotAlt: "AI Score UI",
  },
];

const otherFeatures = [
  {
    icon: (
      <svg
        className="w-5 h-5"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={1.5}
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z"
        />
      </svg>
    ),
    title: "1 Click Generation",
    description:
      "商品を選び、サイズを選び、生成ボタンを押す。3ステップでプロ品質のバナーが完成。",
  },
  {
    icon: (
      <svg
        className="w-5 h-5"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={1.5}
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M6.429 9.75L2.25 12l4.179 2.25m0-4.5l5.571 3 5.571-3m-11.142 0L2.25 7.5 12 2.25l9.75 5.25-4.179 2.25m0 0L12 12.75 6.429 9.75m11.142 0l4.179 2.25L12 17.25 2.25 12l4.179-2.25"
        />
      </svg>
    ),
    title: "Industry Styles",
    description:
      "100万件以上の配信データから業界別の勝ちパターンをスタイル化。全業界対応。",
  },
  {
    icon: (
      <svg
        className="w-5 h-5"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={1.5}
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M19.5 12c0-1.232-.046-2.453-.138-3.662a4.006 4.006 0 00-3.7-3.7 48.678 48.678 0 00-7.324 0 4.006 4.006 0 00-3.7 3.7c-.017.22-.032.441-.046.662M19.5 12l3-3m-3 3l-3-3m-12 3c0 1.232.046 2.453.138 3.662a4.006 4.006 0 003.7 3.7 48.656 48.656 0 007.324 0 4.006 4.006 0 003.7-3.7c.017-.22.032-.441.046-.662M4.5 12l3 3m-3-3l-3 3"
        />
      </svg>
    ),
    title: "Variations",
    description:
      "1つの訴求軸から色味・レイアウト・コピーの異なるバリエーションを自動生成。",
  },
];

const FeaturesSection: React.FC = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const publicUrl = process.env.PUBLIC_URL || "";

  useEffect(() => {
    if (!isAutoPlaying) return;
    const timer = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % spotlightFeatures.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [isAutoPlaying]);

  const active = spotlightFeatures[activeIndex];

  return (
    <section
      id="features"
      className="relative py-28 md:py-44 overflow-hidden"
      style={{ backgroundColor: "#1d1926" }}
    >
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse 50% 50% at 50% 0%, rgba(62,178,152,0.04) 0%, transparent 60%)",
        }}
      />

      <div className="relative max-w-[1200px] mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <span className="inline-flex items-center h-7 px-3 rounded-full bg-white/[0.06] text-white/50 text-[13px] font-medium tracking-[0.04em]">
            Features
          </span>
          <h2 className="mt-5 text-[36px] md:text-[64px] font-extrabold tracking-[-0.03em] text-white leading-[0.95]">
            画像生成ではなく、
            <br />
            <span
              className="bg-clip-text text-transparent"
              style={{
                backgroundImage:
                  "linear-gradient(to right, #8f6fc2, #5a69c1)",
              }}
            >
              広告を設計する。
            </span>
          </h2>
          <p className="mt-5 text-[18px] text-white/50 max-w-lg mx-auto">
            Taskyは単なる画像生成ツールではありません。
            広告プランナーの仕事をAIが一気通貫で実行します。
          </p>
        </motion.div>

        {/* Feature Spotlight Tabs */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          {/* Tab buttons */}
          <div className="flex gap-2 mb-8 overflow-x-auto pb-2">
            {spotlightFeatures.map((feature, i) => (
              <button
                key={feature.id}
                onClick={() => {
                  setActiveIndex(i);
                  setIsAutoPlaying(false);
                }}
                className={`relative flex-shrink-0 px-5 py-3 rounded-xl text-[14px] font-medium transition-all duration-300 ${
                  i === activeIndex
                    ? "bg-white/10 text-white"
                    : "text-white/40 hover:text-white/60 hover:bg-white/[0.04]"
                }`}
              >
                {feature.badge}
                {i === activeIndex && (
                  <motion.div
                    className="absolute bottom-0 left-0 right-0 h-[2px] bg-white/30 rounded-full"
                    layoutId="featureTab"
                    transition={{
                      type: "spring",
                      stiffness: 400,
                      damping: 30,
                    }}
                  />
                )}
              </button>
            ))}
          </div>

          {/* Active feature detail */}
          <div className="rounded-[20px] bg-white/[0.03] overflow-hidden">
            <AnimatePresence mode="wait">
              <motion.div
                key={active.id}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="grid lg:grid-cols-2"
              >
                {/* Left: Content */}
                <div className="p-8 md:p-10 lg:p-12 flex flex-col justify-center">
                  <span
                    className={`inline-flex items-center self-start h-7 px-3 rounded-full text-[14px] font-medium ${active.badgeColor}`}
                  >
                    {active.badge}
                  </span>
                  <h3 className="mt-5 text-[28px] md:text-[36px] font-bold text-white leading-snug">
                    {active.title}
                  </h3>
                  <p className="mt-4 text-[16px] text-white/50 leading-relaxed">
                    {active.description}
                  </p>
                  <ul className="mt-6 space-y-3">
                    {active.details.map((detail) => (
                      <li
                        key={detail}
                        className="flex items-start gap-3 text-[14px] text-white/60"
                      >
                        <svg
                          className="w-4 h-4 mt-0.5 flex-shrink-0 text-[#3eb298]"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                        {detail}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Right: Screenshot */}
                <div className="relative bg-white/[0.02] border-t lg:border-t-0 lg:border-l border-white/[0.04]">
                  <img
                    src={`${publicUrl}${active.screenshot}`}
                    alt={active.screenshotAlt}
                    className="w-full h-full object-cover"
                  />
                </div>
              </motion.div>
            </AnimatePresence>

            {/* Progress bar */}
            <div className="flex gap-1 px-8 md:px-10 lg:px-12 pb-6">
              {spotlightFeatures.map((_, i) => (
                <div
                  key={i}
                  className="flex-1 h-[2px] rounded-full bg-white/[0.06] overflow-hidden"
                >
                  {i === activeIndex && isAutoPlaying ? (
                    <motion.div
                      className="h-full bg-white/30 rounded-full"
                      initial={{ width: "0%" }}
                      animate={{ width: "100%" }}
                      transition={{ duration: 6, ease: "linear" }}
                      key={`progress-${activeIndex}`}
                    />
                  ) : (
                    <div
                      className={`h-full rounded-full transition-all ${
                        i === activeIndex ? "bg-white/30 w-full" : "w-0"
                      }`}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Other features grid */}
        <div className="mt-10 grid md:grid-cols-3 gap-5">
          {otherFeatures.map((feature, i) => (
            <motion.div
              key={feature.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.08 }}
              className="group p-6 rounded-[20px] bg-white/[0.03] hover:bg-white/[0.05] hover:-translate-y-0.5 transition-all duration-300"
            >
              <span className="text-white/40">{feature.icon}</span>
              <h3 className="mt-4 text-[20px] font-bold text-white">
                {feature.title}
              </h3>
              <p className="mt-2 text-[14px] text-white/40 leading-relaxed">
                {feature.description}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FeaturesSection;
